import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { getKeyCombinationByPlatform } from 'v3/utils/os';
import { getLocalStorage } from 'common/utils/localstorage';
import { ACTIVE_BROKERS_DOMAINS } from '../types.type';
import { LandingPageProps } from 'v3/pages/LandingPage/types.type';
import { getLoginModal } from './config';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';

import { findBrokerConfigById } from './LoginModal/config';

export const useUserAuth = ({ shouldOpenLoginModalByDefault }: Partial<LandingPageProps>) => {
  const [isShowLoginModal, setIsShowLoginModal] = useState(shouldOpenLoginModalByDefault ?? false);
  const [goToCredentialPhase, setGoToCredentialPhase] = useState<boolean>(false);

  const { currentDomain } = window;

  // This is to check the subdomain
  const brokerDomain =
    (_.size(ApplicationDomainUtility.supportedBrokers) === 1 &&
      ApplicationDomainUtility.supportedBrokers[0]) ||
    currentDomain;

  const brokerIdFromDomain =
    ACTIVE_BROKERS_DOMAINS[_.toUpper(brokerDomain) as keyof typeof ACTIVE_BROKERS_DOMAINS];

  const domainOrPreviousLoggedInBrokerId = brokerIdFromDomain
    ? brokerIdFromDomain
    : ApplicationDomainUtility.isCrypto
      ? ACTIVE_BROKERS_DOMAINS.DELTAEXCHANGE
      : (getLocalStorage('domainOrPreviousLoggedInBrokerId') as ACTIVE_BROKERS_DOMAINS);

  // These to handle the suggested broker id when user clicks on suggested broker logo
  const suggestedBrokerId = useRef<ACTIVE_BROKERS_DOMAINS | null>(null);

  // This brokerIdFromDomain is passed from the SuggestedBroker component
  // This handles the onclick on broker logos and prev logged in brokers
  const handlePreviouslyLoggedInBroker = (brokerId?: ACTIVE_BROKERS_DOMAINS) => {
    let selectedBrokerConfig: ReturnType<typeof findBrokerConfigById>;

    switch (true) {
      // This is to handle the suggested broker id when user clicks on suggested broker logo
      case _.includes(ACTIVE_BROKERS_DOMAINS, brokerId) && brokerId !== undefined:
        suggestedBrokerId.current = brokerId;
        selectedBrokerConfig = findBrokerConfigById(brokerId);
        break;
      // This is to handle the subdomain broker login and previously logged in brokers
      case !!domainOrPreviousLoggedInBrokerId:
        selectedBrokerConfig = findBrokerConfigById(
          domainOrPreviousLoggedInBrokerId as ACTIVE_BROKERS_DOMAINS
        );
        break;
      default:
        break;
    }

    if (!selectedBrokerConfig?.href) {
      setGoToCredentialPhase(true);
      return;
    }

    // hit the broker login phase
    getLoginModal(selectedBrokerConfig);
    // This is being reset to null to avoid any conflicts while opening the modal next time
    suggestedBrokerId.current = null;
  };

  const hideLoginModal = () => {
    if (goToCredentialPhase) setGoToCredentialPhase(false);
    setIsShowLoginModal(false);
    // This is being reset to null to avoid any conflicts while opening the modal next time
    suggestedBrokerId.current = null;
  };

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const loginBtn = document.querySelector<HTMLButtonElement>('.change-broker-btn-shortcut');
      if (!loginBtn) return;

      try {
        const key = getKeyCombinationByPlatform(event);
        if (key && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          loginBtn.click();
        }
      } catch (error) {
        console.error('Error handling keyboard shortcut:', error);
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  return {
    isShowLoginModal,
    setIsShowLoginModal,
    domainOrPreviousLoggedInBrokerId,
    handlePreviouslyLoggedInBroker,
    goToCredentialPhase,
    hideLoginModal,
    isSubDomain: !!brokerIdFromDomain,
    suggestedBrokerId: suggestedBrokerId.current,
  };
};
